import {assert} from 'Asserts';
import {RequestV2StatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import DocumentStore from 'notebook/documents/DocumentStore';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import hasBlockingValidationErrors from 'notebook/structured-tables/util/hasBlockingValidationErrors';
import tableHasUnsubmittedChanges from 'notebook/structured-tables/util/tableHasUnsubmittedChanges';
import {EvaluatedTable} from 'notebook/tables/excess/EvaluationConstants';
import {isStructuredTable} from 'notebook/TypePredicates';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';

import {RequestV2SubmissionDetails} from './RequestV2SubmissionDetailsQueryUtils';

export const MAX_REQUEST_SUBMISSION_NAME_LENGTH = 255;

export function getTaskTableFromRequestDocument(documentId: string): {
  taskTable: ProcessTaskStructuredTable;
  computedTable: EvaluatedTable;
} | null {
  const document = DocumentStore.getDocument(documentId);
  if (document == null) {
    return null;
  }
  assert(document != null, 'Expected document to be present');
  let taskTable: ProcessTaskStructuredTable | null = null;
  for (const notePart of document.documentVersion.entryVersion.note.noteParts ?? []) {
    if (notePart.item != null && isStructuredTable(notePart.item)) {
      const table = notePart.item;
      if (table.kind === StructuredTableKind.PROCESS_TASK) {
        assert(taskTable == null, 'Expected only one task table to be present in the document');
        taskTable = table as ProcessTaskStructuredTable;
      }
    }
  }

  assert(taskTable != null, 'Expected a task table to be present in the document');
  const computedTable =
    document.documentVersion.entryVersion.note.noteRuntimeSnapshot.evaluationSnapshot?.[taskTable.name];
  assert(computedTable != null, 'Expected a computed table to correspond to the task table');
  return {taskTable, computedTable};
}

export function isTaskTableBlockingSubmission(
  taskTable: ProcessTaskStructuredTable,
  computedTable: EvaluatedTable
): boolean {
  const hasUnsubmittedChanges = tableHasUnsubmittedChanges(taskTable, computedTable);
  const hasErrors = hasBlockingValidationErrors(computedTable);
  return hasUnsubmittedChanges || hasErrors;
}

export function isSendDisabledForRequest(documentId: string): boolean {
  const tables = getTaskTableFromRequestDocument(documentId);
  if (tables == null) {
    return true;
  }
  const {taskTable, computedTable} = tables;
  return isTaskTableBlockingSubmission(taskTable, computedTable);
}

export function isRequestSubmissionLocked(requestV2Submission: RequestV2SubmissionDetails): boolean {
  return (
    requestV2Submission.archiveData.archived ||
    requestV2Submission.status !== RequestV2StatusType.DRAFT ||
    !requestV2Submission.permissions.writable
  );
}
